import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Send, RefreshCcw, Loader2, RotateCcw, Power, Settings as SettingsIcon, AlertTriangle, ArrowRightLeft } from 'lucide-react';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { useShowMore } from '../../components/useShowMore';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmtUSD = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const fmtCT = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return '—'; }
};

const STATUS_BADGE = {
    pending_accept: { label: 'Pending',   color: '#FFD700', bg: 'bg-[#FFD700]/15', border: 'border-[#FFD700]/40' },
    accepted:       { label: 'Accepted',  color: '#39FF14', bg: 'bg-[#39FF14]/15', border: 'border-[#39FF14]/40' },
    declined:       { label: 'Declined',  color: '#FF3B30', bg: 'bg-[#FF3B30]/15', border: 'border-[#FF3B30]/40' },
    expired:        { label: 'Expired',   color: '#A0AAB2', bg: 'bg-white/5',      border: 'border-white/10' },
    canceled:       { label: 'Canceled',  color: '#A0AAB2', bg: 'bg-white/5',      border: 'border-white/10' },
    reversed:       { label: 'Reversed',  color: '#FF3B30', bg: 'bg-[#FF3B30]/15', border: 'border-[#FF3B30]/40' },
};

const PeerTransfersAdminPanel = () => {
    const [data, setData] = useState({ rows: [], count: 0, total_sent: 0, total_accepted: 0 });
    const [loading, setLoading] = useState(true);
    const [days, setDays] = useState(7);
    const [statusFilter, setStatusFilter] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [settings, setSettings] = useState(null);
    const [showSettings, setShowSettings] = useState(false);
    const [saving, setSaving] = useState(false);
    const [clusters, setClusters] = useState({ rows: [], count: 0 });
    const [clusterMinCount, setClusterMinCount] = useState(5);

    const fetchClusters = useCallback(async () => {
        try {
            const { data: c } = await axios.get(
                `${API}/admin/peer-transfers/clusters?days=${days}&min_count=${clusterMinCount}`,
                { withCredentials: true }
            );
            setClusters(c);
        } catch { /* non-critical */ }
    }, [days, clusterMinCount]);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const q = new URLSearchParams({ days: String(days) });
            if (statusFilter) q.append('status', statusFilter);
            const { data: resp } = await axios.get(`${API}/admin/peer-transfers?${q}`, { withCredentials: true });
            setData(resp);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to load');
        }
        setLoading(false);
    }, [days, statusFilter]);

    const fetchSettings = useCallback(async () => {
        try {
            const { data: s } = await axios.get(`${API}/admin/peer-transfers/settings`, { withCredentials: true });
            setSettings(s);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { fetchData(); }, [fetchData]);
    useEffect(() => { fetchSettings(); }, [fetchSettings]);
    useEffect(() => { fetchClusters(); }, [fetchClusters]);

    const reverseTransfer = async (row) => {
        const reason = window.prompt(
            `Reverse this $${row.amount.toFixed(2)} transfer from ${row.sender_name} → ${row.recipient_name}?\n\n` +
            `Recipient will lose $${row.amount.toFixed(2)}, sender gets it back.\n\n` +
            `REQUIRED: brief reason for the audit log:`
        );
        if (!reason || !reason.trim()) return;
        setBusyId(row.id);
        try {
            await axios.post(
                `${API}/admin/peer-transfers/${row.id}/reverse`,
                { reason: reason.trim() },
                { withCredentials: true }
            );
            toast.success(`Reversed — ${row.sender_name} refunded $${row.amount.toFixed(2)}`);
            await fetchData();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Reversal failed');
        }
        setBusyId(null);
    };

    const saveSettings = async (patch) => {
        setSaving(true);
        try {
            const { data: s } = await axios.put(
                `${API}/admin/peer-transfers/settings`,
                patch,
                { withCredentials: true }
            );
            setSettings(s);
            toast.success('Settings saved');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Could not save');
        }
        setSaving(false);
    };

    const { visibleItems, ShowMoreButton, total: rowCount, limit } = useShowMore(data.rows || [], 10, 10);

    return (
        <div className="card-dark p-6 mb-4" data-testid="peer-transfers-admin-panel">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="text-base font-bold text-white flex items-center gap-2">
                        <Send className="w-5 h-5 text-[#FFD700]" />
                        Peer Transfers
                        {settings && !settings.enabled && (
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-black bg-[#FF3B30]/20 text-[#FF3B30] border border-[#FF3B30]/40 ml-2">
                                DISABLED
                            </span>
                        )}
                    </h3>
                    <p className="text-[11px] text-[#A0AAB2] mt-1">
                        Player-to-player credit transfers. {data.count} in window · {fmtUSD(data.total_accepted)} accepted
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <select
                        value={days}
                        onChange={(e) => setDays(Number(e.target.value))}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        data-testid="peer-admin-days"
                    >
                        <option value={1}>1 day</option>
                        <option value={7}>7 days</option>
                        <option value={30}>30 days</option>
                        <option value={90}>90 days</option>
                    </select>
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-black/40 border border-white/10 rounded-md px-2 py-1 text-xs text-white"
                        data-testid="peer-admin-status"
                    >
                        <option value="">All status</option>
                        <option value="pending_accept">Pending</option>
                        <option value="accepted">Accepted</option>
                        <option value="declined">Declined</option>
                        <option value="expired">Expired</option>
                        <option value="canceled">Canceled</option>
                        <option value="reversed">Reversed</option>
                    </select>
                    <Button size="sm" variant="outline" onClick={() => setShowSettings((v) => !v)} className="border-white/10 text-white hover:bg-white/5" data-testid="peer-admin-settings-toggle">
                        <SettingsIcon className="w-3.5 h-3.5 mr-1" /> Settings
                    </Button>
                    <button onClick={fetchData} className="p-2 rounded-md border border-white/10 text-[#A0AAB2] hover:text-white hover:bg-white/5" title="Refresh">
                        <RefreshCcw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {showSettings && settings && (
                <div className="mb-4 p-4 rounded-lg bg-black/30 border border-white/10 space-y-3" data-testid="peer-admin-settings-panel">
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-white font-bold text-sm flex items-center gap-2">
                                <Power className="w-4 h-4 text-[#FFD700]" />
                                Kill switch
                            </div>
                            <p className="text-[10px] text-[#A0AAB2] mt-0.5">When off, players can't send new transfers (existing pending ones still resolvable).</p>
                        </div>
                        <Button
                            size="sm"
                            onClick={() => saveSettings({ enabled: !settings.enabled })}
                            disabled={saving}
                            className={settings.enabled ? 'bg-[#39FF14] text-black hover:bg-[#39FF14]/80' : 'bg-[#FF3B30] text-white hover:bg-[#FF3B30]/80'}
                            data-testid="peer-admin-enabled-toggle"
                        >
                            {settings.enabled ? 'ENABLED' : 'DISABLED'}
                        </Button>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <SettingField label="Min ($)" value={settings.min_amount} step="0.5" min="0" onSave={(v) => saveSettings({ min_amount: v })} disabled={saving} />
                        <SettingField label="Max / transfer ($)" value={settings.max_amount_per_transfer} step="10" min="1" onSave={(v) => saveSettings({ max_amount_per_transfer: v })} disabled={saving} />
                        <SettingField label="Daily cap ($)" value={settings.daily_sender_cap} step="100" min="0" onSave={(v) => saveSettings({ daily_sender_cap: v })} disabled={saving} />
                        <SettingField label="Accept window (h)" value={settings.acceptance_window_hours} step="1" min="1" max="168" onSave={(v) => saveSettings({ acceptance_window_hours: v })} disabled={saving} />
                    </div>
                </div>
            )}

            {/* Frequent transfer pairs — collusion / funnel detector */}
            {clusters.count > 0 && (
                <div className="mb-4 rounded-lg border-2 border-[#FF3B30]/40 bg-[#FF3B30]/5 p-4" data-testid="peer-clusters-panel">
                    <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                        <div className="flex items-center gap-2">
                            <AlertTriangle className="w-5 h-5 text-[#FF3B30]" />
                            <div>
                                <h4 className="text-sm font-black text-white">
                                    Frequent transfer pairs ({clusters.count})
                                </h4>
                                <p className="text-[10px] text-[#A0AAB2]">
                                    {clusterMinCount}+ transfers between the same two players in {days} day{days === 1 ? '' : 's'}. Could be friends — or could be collusion. Review.
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-1">
                            <span className="text-[10px] text-[#A0AAB2] mr-1">Threshold:</span>
                            {[3, 5, 10].map((n) => (
                                <button
                                    key={n}
                                    type="button"
                                    onClick={() => setClusterMinCount(n)}
                                    className={`px-2 py-0.5 rounded-full text-[10px] font-black border transition-colors ${
                                        clusterMinCount === n
                                            ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                                            : 'bg-transparent text-[#FF3B30] border-[#FF3B30]/40 hover:bg-[#FF3B30]/10'
                                    }`}
                                    data-testid={`peer-cluster-threshold-${n}`}
                                >
                                    {n}+
                                </button>
                            ))}
                        </div>
                    </div>
                    <div className="space-y-1.5">
                        {clusters.rows.slice(0, 6).map((c) => (
                            <div
                                key={`${c.sender_id}-${c.recipient_id}`}
                                className="rounded-md bg-black/30 border border-white/10 p-2.5 flex items-center justify-between gap-3 flex-wrap"
                                data-testid={`peer-cluster-${c.sender_id}-${c.recipient_id}`}
                            >
                                <div className="flex items-center gap-2 min-w-0">
                                    <div className="text-xs text-white font-bold flex items-center gap-1.5 flex-wrap">
                                        <span>{c.sender_name}</span>
                                        <ArrowRightLeft className={`w-3 h-3 ${c.reciprocal ? 'text-[#FF3B30]' : 'text-[#A0AAB2]'}`} />
                                        <span>{c.recipient_name}</span>
                                        {c.reciprocal && (
                                            <span className="px-1.5 py-0 rounded text-[9px] font-black bg-[#FF3B30]/30 text-[#FF3B30] border border-[#FF3B30]/50">
                                                BOTH WAYS
                                            </span>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-3 text-[11px] font-mono">
                                    <span className="text-[#FFD700]">
                                        <span className="text-[#A0AAB2] text-[9px] uppercase tracking-wider mr-1">total</span>
                                        {fmtUSD(c.total_amount)}
                                    </span>
                                    <span className="text-white">
                                        <span className="text-[#A0AAB2] text-[9px] uppercase tracking-wider mr-1">x</span>
                                        {c.count}
                                        {c.accepted_count !== c.count && (
                                            <span className="text-[#A0AAB2]"> ({c.accepted_count} acc.)</span>
                                        )}
                                    </span>
                                </div>
                            </div>
                        ))}
                        {clusters.rows.length > 6 && (
                            <p className="text-[10px] text-[#A0AAB2] text-center">+{clusters.rows.length - 6} more pairs</p>
                        )}
                    </div>
                </div>
            )}

            {loading && data.rows.length === 0 ? (
                <div className="flex justify-center py-6"><Loader2 className="w-6 h-6 animate-spin text-[#FFD700]" /></div>
            ) : data.rows.length === 0 ? (
                <p className="text-center text-[#A0AAB2] text-sm py-6">No peer transfers in this window.</p>
            ) : (
                <div className="overflow-x-auto">
                    <Table>
                        <TableHeader>
                            <TableRow className="border-white/10">
                                <TableHead className="text-[#A0AAB2]">When</TableHead>
                                <TableHead className="text-[#A0AAB2]">Sender → Recipient</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Amount</TableHead>
                                <TableHead className="text-[#A0AAB2]">Status</TableHead>
                                <TableHead className="text-[#A0AAB2]">Memo</TableHead>
                                <TableHead className="text-[#A0AAB2] text-right">Action</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {visibleItems.map((r) => {
                                const b = STATUS_BADGE[r.status] || STATUS_BADGE.expired;
                                return (
                                    <TableRow key={r.id} className="border-white/10" data-testid={`peer-row-${r.id}`}>
                                        <TableCell className="text-[#A0AAB2] text-xs">{fmtCT(r.created_at)}</TableCell>
                                        <TableCell>
                                            <div className="text-white text-sm">{r.sender_name}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">→ {r.recipient_name}</div>
                                            <div className="text-[10px] text-[#A0AAB2]">{r.recipient_email}</div>
                                            {r.linked_accounts?.length > 0 && (
                                                <div className="mt-1 inline-block px-1.5 py-0.5 rounded bg-red-500/15 border border-red-500/40 text-[10px] font-bold text-red-300" data-testid={`peer-linked-${r.id}`}>
                                                    LINKED ACCOUNTS · {r.linked_accounts.join(', ')}
                                                </div>
                                            )}
                                        </TableCell>
                                        <TableCell className="text-right font-mono font-black text-[#FFD700]">{fmtUSD(r.amount)}</TableCell>
                                        <TableCell>
                                            <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${b.bg} ${b.border}`} style={{ color: b.color }}>
                                                {b.label}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-[#A0AAB2] text-xs italic max-w-xs truncate" title={r.memo || ''}>{r.memo || '—'}</TableCell>
                                        <TableCell className="text-right">
                                            {r.status === 'accepted' && (
                                                <Button
                                                    size="sm"
                                                    variant="outline"
                                                    onClick={() => reverseTransfer(r)}
                                                    disabled={busyId === r.id}
                                                    className="border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/10"
                                                    data-testid={`peer-reverse-${r.id}`}
                                                    title="Reverse within 24h of acceptance"
                                                >
                                                    {busyId === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <><RotateCcw className="w-3 h-3 mr-1" /> Reverse</>}
                                                </Button>
                                            )}
                                            {r.status === 'reversed' && r.reversed_reason && (
                                                <span className="text-[10px] text-[#A0AAB2] italic" title={r.reversed_reason}>
                                                    "{r.reversed_reason.slice(0, 30)}{r.reversed_reason.length > 30 ? '…' : ''}"
                                                </span>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                    <p className="text-[10px] text-[#A0AAB2] text-center mt-2">
                        Showing {Math.min(limit, rowCount)} of {rowCount}
                    </p>
                    <ShowMoreButton testid="peer-admin-show-more" />
                </div>
            )}
        </div>
    );
};

const SettingField = ({ label, value, step, min, max, onSave, disabled }) => {
    const [v, setV] = useState(value);
    useEffect(() => { setV(value); }, [value]);
    const commit = () => {
        const n = parseFloat(v);
        if (!isNaN(n) && n !== Number(value)) onSave(n);
    };
    return (
        <div>
            <label className="block text-[9px] uppercase tracking-wider text-[#A0AAB2] font-bold mb-1">{label}</label>
            <Input
                type="number"
                value={v}
                step={step}
                min={min}
                max={max}
                disabled={disabled}
                onChange={(e) => setV(e.target.value)}
                onBlur={commit}
                onKeyDown={(e) => e.key === 'Enter' && commit()}
                className="bg-black/40 border-white/10 text-white h-9 text-sm font-mono"
            />
        </div>
    );
};

export default PeerTransfersAdminPanel;
